import React, { FC, useEffect, useRef, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';

import { AddressesSuggestionsItem } from '@domains/addresses';

import { useCommonData } from '@stores/global-data';
import { $profile } from '@stores/profile';

import { apiAddressesSuggestions } from '@api/methods';

import { isValueSet } from '@utils/guards';
import { SnackBar } from '@utils/snackbar';

import { useClickOutside } from '@hooks/click-outside';
import { useDebounce } from '@hooks/use-debounce';

import { Spinner } from '@components/common/Spinner/Spinner';
import { StyledInput } from '@components/common/StyledInput/StyledInput';

import styles from './AddressSearchInput.module.scss';

export type AddressSearchInputProps = {
    cityFiasId: string;
    onChange?(value: AddressesSuggestionsItem | null): void;
    className?: string;
    hintMessage?: string;
    hasError?: boolean;
    disabled?: boolean;
};

export const AddressSearchInput: FC<AddressSearchInputProps> = (props) => {
    const { onChange, className, cityFiasId, hintMessage, hasError, disabled } = props;

    const [isCityFiasIdSomeTimeChanged, setIsCityFiasIdSomeTimeChanged] = useState<boolean>(false);
    const [addressString, setAddressString] = useState<string>('');
    const [debouncedAddressString] = useDebounce(addressString, 500);
    const [selected, setSelected] = useState<AddressesSuggestionsItem | null>(null);
    const [items, setItems] = useState<AddressesSuggestionsItem[]>([]);
    const [isPreventFetch, setIsPreventFetch] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isShowList, setIsShowList] = useState(false);

    const { editedAddress } = useUnit($profile);
    const { cities } = useCommonData();

    const ref = useRef<HTMLDivElement>(null);

    const isListHidden = !isShowList && (!isLoading || items.length === 0);

    const setFormattedAddressString = (suggestionItem: AddressesSuggestionsItem | null) => {
        if (suggestionItem === null) {
            setAddressString('');
            return;
        }

        const { street, house } = suggestionItem;

        setAddressString(
            `${isValueSet(street) ? `${street},` : ''} ${isValueSet(house) ? house : ''}`,
        );
    };

    const handleChangeOption = (item: AddressesSuggestionsItem) => {
        if (onChange !== undefined) onChange(item);

        setFormattedAddressString(item);
        setSelected(item);
        setIsShowList(false);
        if (item.house !== null) {
            setIsPreventFetch(true);
        }
    };

    useClickOutside({
        ref: ref,
        action: () => {
            setIsShowList(false);

            if (selected === null && items.length > 0) {
                setFormattedAddressString(items[0]);
            } else {
                setFormattedAddressString(selected);
            }
        },
    });

    useEffect(() => {
        if (!isPreventFetch && debouncedAddressString.length > 0) {
            setIsLoading(true);
            setIsShowList(true);
            apiAddressesSuggestions({
                city_fias_id: cityFiasId,
                query: debouncedAddressString,
            })
                .then((result) => setItems(result.items))
                .catch(SnackBar.handleApiError)
                .finally(() => setIsLoading(false));
        } else if (debouncedAddressString.length === 0) {
            setIsShowList(false);
        }

        setIsPreventFetch(false);
    }, [debouncedAddressString]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setFormattedAddressString(null);
        setSelected(null);
    }, [cityFiasId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isCityFiasIdSomeTimeChanged && editedAddress !== null && cities.length > 0) {
            const city = cities.find((item) => item.id === editedAddress.city_id) ?? cities[0];

            if (city.fias_id !== cityFiasId) {
                setIsCityFiasIdSomeTimeChanged(true);
                return;
            }

            const item: AddressesSuggestionsItem = {
                city_fias_id: city.fias_id,
                city: city.name,
                street: editedAddress.street,
                house: editedAddress.house,
            };

            setSelected(item);
            setFormattedAddressString(item);
            setIsPreventFetch(true);
        }
    }, [cities, editedAddress, cityFiasId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={clsx(styles.root, className)} ref={ref}>
            <StyledInput
                value={addressString}
                onChange={(e) => setAddressString(e.target.value)}
                label="Улица и дом"
                classes={{
                    actionIcon: styles.icon,
                    hintMessage: styles.hintMessage,
                }}
                hintMessage={hintMessage}
                hasError={hasError}
                disabled={disabled}
                data-testid="address-search-input"
            />
            <div className={clsx(styles.list, isListHidden && styles.isHidden)}>
                {isLoading && <Spinner classes={{ root: styles.spinner }} />}
                {!isLoading && items.length === 0 && (
                    <p className={styles.notFound}>Ничего не найдено...</p>
                )}
                {!isLoading &&
                    items.map((item, index) => (
                        <button
                            type="button"
                            className={clsx(
                                styles.option,
                                item.street === selected?.street &&
                                    item.house === selected?.house &&
                                    styles.isSelected,
                            )}
                            key={index}
                            onClick={() => handleChangeOption(item)}
                            data-testid="address-suggestion-item"
                        >
                            <p>
                                {item.street}, {item.house}
                            </p>
                            <p className={styles.city}>{item.city}</p>
                        </button>
                    ))}
            </div>
        </div>
    );
};
